import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Loading } from "@/components/common/Loading";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";
import { TagPicker } from "@/components/tags/TagPicker";

export default function AdminEntitlements() {
  const [data, setData] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/entitlements"); setData(data); }
    catch { setData({ tiers: [], tags: [], entitlements: [] }); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    (data?.entitlements || []).forEach((e) => { (g[e.category] = g[e.category] || []).push(e); });
    return g;
  }, [data]);

  const patchEnt = (key, patch) => {
    setData((d) => ({ ...d, entitlements: d.entitlements.map((e) => e.key === key ? { ...e, ...patch } : e) }));
  };

  const toggleTier = async (ent, tierKey) => {
    const has = ent.allowed_tiers.includes(tierKey);
    const next = has ? ent.allowed_tiers.filter((t) => t !== tierKey) : [...ent.allowed_tiers, tierKey];
    patchEnt(ent.key, { allowed_tiers: next }); // optimistic
    try { await api.patch(`/admin/entitlements/${ent.key}`, { allowed_tiers: next }); }
    catch { toast.error("Failed to update"); load(); }
  };

  const setTags = async (ent, nextTags) => {
    patchEnt(ent.key, { allowed_tags: nextTags }); // optimistic
    try { await api.patch(`/admin/entitlements/${ent.key}`, { allowed_tags: nextTags }); }
    catch { toast.error("Failed to update tag grants"); load(); }
  };

  if (!data) return <Loading />;
  const tiers = data.tiers || [];
  const tags = data.tags || [];

  return (
    <div data-testid="admin-entitlements" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Check a tier to let it use the action. Add an <span className="font-[600] text-foreground">account tag</span> under
          “Tag grants” to unlock the action for anyone holding that tag — regardless of plan. Great for beta groups. Changes apply instantly.
        </p>
        <AdminRefreshButton testId="admin-refresh-entitlements" onRefresh={load} />
      </div>
      {Object.entries(grouped).map(([cat, ents]) => (
        <Card key={cat} className="border-0 p-0">
          <div className="border-b border-border px-5 py-3"><h2 className="text-sm font-[680] uppercase tracking-wide text-muted-foreground">{cat}</h2></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-2.5 font-[600]">Action</th>
                  {tiers.map((t) => <th key={t.key} className="px-4 py-2.5 text-center font-[600]">{t.label}</th>)}
                  <th className="min-w-[200px] px-4 py-2.5 font-[600]">Tag grants</th>
                </tr>
              </thead>
              <tbody>
                {ents.map((e) => (
                  <tr key={e.key} className="border-t border-border/60" data-testid={`admin-ent-${e.key}`}>
                    <td className="px-5 py-3"><div className="font-[540] text-foreground">{e.label}</div><div className="font-mono text-[11px] text-muted-foreground">{e.key}</div></td>
                    {tiers.map((t) => (
                      <td key={t.key} className="px-4 py-3 text-center">
                        <Checkbox checked={e.allowed_tiers.includes(t.key)} onCheckedChange={() => toggleTier(e, t.key)} data-testid={`admin-ent-${e.key}-${t.key}`} />
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <TagPicker
                        allTags={tags}
                        value={e.allowed_tags || []}
                        onChange={(ids) => setTags(e, ids)}
                        compact
                        testId={`admin-ent-tags-${e.key}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
      {tags.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="admin-ent-no-tags">
          No active account tags yet — create some in <span className="font-[600]">Admin → Account Tags</span> to use tag grants.
        </p>
      )}
    </div>
  );
}
